<template>
  <div class="suggestion-undo-banner" role="status" data-testid="suggestion-undo-banner">
    <span class="suggestion-undo-banner__icon" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="9" stroke="#7f56d9" stroke-width="2" />
        <path d="M10 9V14M10 6.5V6.6" stroke="#7f56d9" stroke-width="2" stroke-linecap="round" />
      </svg>
    </span>
    <div class="suggestion-undo-banner__content">
      <p class="suggestion-undo-banner__title">{{ title }}</p>
      <p v-if="message" class="suggestion-undo-banner__message">{{ message }}</p>
      <button
        type="button"
        class="suggestion-undo-banner__action"
        data-testid="suggestion-undo-action"
        :disabled="busy"
        :aria-busy="busy ? 'true' : 'false'"
        @click="onUndo"
      >
        {{ actionLabel }}
      </button>
    </div>
    <button
      type="button"
      class="suggestion-undo-banner__close"
      :aria-label="dismissLabel"
      data-testid="suggestion-undo-dismiss"
      @click="emit('dismiss')"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M18 6 6 18M6 6l12 12"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>

<script setup>
const props = defineProps({
  title: { type: String, required: true },
  message: { type: String, default: "" },
  actionLabel: { type: String, default: "Undo Suggestions" },
  dismissLabel: { type: String, default: "Dismiss suggestions" },
  /** When true the undo request is in flight and the action is disabled. */
  busy: { type: Boolean, default: false },
});

const emit = defineEmits(["undo", "dismiss"]);

function onUndo() {
  if (props.busy) return;
  emit("undo");
}
</script>

<style scoped>
.suggestion-undo-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #ffffff;
}

.suggestion-undo-banner__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.suggestion-undo-banner__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggestion-undo-banner__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.suggestion-undo-banner__message {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.suggestion-undo-banner__action {
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #6941c6;
  cursor: pointer;
}

.suggestion-undo-banner__action:hover:not(:disabled) {
  text-decoration: underline;
}

.suggestion-undo-banner__action:disabled {
  color: #98a2b3;
  cursor: default;
}

.suggestion-undo-banner__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
}

.suggestion-undo-banner__close:hover {
  background: #f2f4f7;
}

.suggestion-undo-banner__action:focus-visible,
.suggestion-undo-banner__close:focus-visible {
  outline: 2px solid #6845c8;
  outline-offset: 1px;
}
</style>
